import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaUser,
  FaCalendar,
  FaEye,
  FaUsers,
  FaProjectDiagram
} from 'react-icons/fa';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';

interface User {
  _id: string;
  name: string;
  email: string;
  paymentProvider?: string;
  role: string;
  firebaseUid?: string;
  emailVerified: boolean;
  photoURL?: string;
  providerId?: string;
  disabled: boolean;
  organization?: {
    name: string;
    teamMembers: any[];
  };
  createdAt: string;
  updatedAt: string;
  __v?: number;
}

const UserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [userProjects, setUserProjects] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
    }
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      setIsLoading(true);
      
      console.log(`Fetching user details for ID: ${userId}`);
      
      const userResponse = await api.get(`/admin/users/${userId}?include=projects`);
      const userData = userResponse.data.data || userResponse.data;

      setUser(userData);

      if (userData.projects && Array.isArray(userData.projects)) {
        setUserProjects(userData.projects);
      } else {
        setUserProjects([]);
      }
      
    } catch (error) {
      console.error('Error fetching user details:', error);
      
      // Fallback: Try to fetch user without includes
      try {
        console.log('Trying to fetch user without includes...');
        const fallbackResponse = await api.get(`/admin/users/${userId}`);
        const fallbackUserData = fallbackResponse.data.data || fallbackResponse.data;
        setUser(fallbackUserData);
        setUserProjects([]);

      } catch (fallbackError) {
        console.error('Fallback user fetch also failed:', fallbackError);
        setUser(null);
        setUserProjects([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleBadge = (role: string) => {
    const roleConfig = {
      admin: { color: 'bg-red-100 text-red-800', label: 'Admin' },
      agency_admin: { color: 'bg-blue-100 text-blue-800', label: 'Agency Admin' },
      agency_member: { color: 'bg-green-100 text-green-800', label: 'Agency Member' },
      member: { color: 'bg-gray-100 text-gray-800', label: 'Member' }
    };

    const config = roleConfig[role as keyof typeof roleConfig] || roleConfig.member;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (name: string) => {
    if (!name) return 'NA';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">User not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-lg p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/users')}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <FaUsers className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">User Details</h1>
              <p className="text-gray-600 mt-1">View comprehensive user information</p>
            </div>
          </div>
        </div>

        {/* User Profile Card */}
        <div className="bg-gradient-to-r from-primary to-primaryDark rounded-xl p-6 text-white">
          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-white text-2xl font-bold">
              {getInitials(user.name)}
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold">{user.name}</h2>
              <p className="text-primaryLight">{user.email}</p>
              <div className="flex items-center space-x-4 mt-2">
                {getRoleBadge(user.role)}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* User Information Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Information */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-lg p-6"
        >
          <div className="flex items-center space-x-3 mb-6">
            <FaUser className="h-6 w-6 text-primary" />
            <h3 className="text-xl font-semibold text-gray-900">Basic Information</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Full Name</label>
              <p className="text-gray-900 font-medium">{user.name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Email Address</label>
              <p className="text-gray-900 font-medium">{user.email}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Role</label>
              <div className="mt-1">{getRoleBadge(user.role)}</div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Account Status</label>
              <div className="mt-1">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  !user.disabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {!user.disabled ? 'Active' : 'Disabled'}
                </span>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Email Verified</label>
              <div className="mt-1">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  user.emailVerified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {user.emailVerified ? 'Verified' : 'Not Verified'}
                </span>
              </div>
            </div>
            {user.firebaseUid && (
              <div>
                <label className="text-sm font-medium text-gray-500">Firebase UID</label>
                <p className="text-gray-900 font-medium font-mono text-sm">{user.firebaseUid}</p>
              </div>
            )}
            {user.providerId && (
              <div>
                <label className="text-sm font-medium text-gray-500">Provider ID</label>
                <p className="text-gray-900 font-medium">{user.providerId}</p>
              </div>
            )}
            {user.paymentProvider && (
              <div>
                <label className="text-sm font-medium text-gray-500">Payment Provider</label>
                <p className="text-gray-900 font-medium capitalize">{user.paymentProvider}</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Organization Information */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-lg p-6"
        >
          <div className="flex items-center space-x-3 mb-6">
            <FaUsers className="h-6 w-6 text-primary" />
            <h3 className="text-xl font-semibold text-gray-900">Organization</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Organization Name</label>
              <p className="text-gray-900 font-medium">{user.organization?.name || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Team Members</label>
              <div className="text-gray-900 font-medium">
                {user.organization?.teamMembers?.length || 0} members
                {user.organization?.teamMembers && user.organization.teamMembers.length > 0 && (
                  <div className="mt-2">
                    <div className="flex flex-wrap gap-2">
                      {user.organization.teamMembers.map((member, index) => (
                        <span
                          key={member._id || index}
                          className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 hover:bg-primary hover:text-white transition-colors cursor-pointer"
                          onClick={() => navigate(`/users/${member._id}`)}
                        >
                          {member.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Account Information */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl shadow-lg p-6"
        >
          <div className="flex items-center space-x-3 mb-6">
            <FaCalendar className="h-6 w-6 text-primary" />
            <h3 className="text-xl font-semibold text-gray-900">Account Information</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Member Since</label>
              <p className="text-gray-900 font-medium">{formatDate(user.createdAt)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Last Updated</label>
              <p className="text-gray-900 font-medium">{formatDate(user.updatedAt)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">User ID</label>
              <p className="text-gray-900 font-medium font-mono text-sm">{user._id}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* User's Projects */}
      {userProjects.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-2xl shadow-lg p-6"
        >
          <div className="flex items-center space-x-3 mb-6">
            <FaProjectDiagram className="h-6 w-6 text-primary" />
            <h3 className="text-xl font-semibold text-gray-900">User's Projects ({userProjects.length})</h3>
          </div>
          <div className="space-y-4">
            {userProjects.map((project, index) => (
              <div
                key={project._id || index}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => navigate(`/projects/${project._id}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{project.projectName || project.name}</h4>
                    <p className="text-sm text-gray-500 mt-1">{project.description?.substring(0, 100)}...</p>
                    <div className="flex items-center space-x-4 mt-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        project.status === 'active' ? 'bg-green-100 text-green-800' :
                        project.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                        project.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {project.status || 'Draft'}
                      </span>
                      <span className="text-xs text-gray-500">
                        Created: {formatDate(project.createdAt)}
                      </span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <FaEye className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Team Members */}
      {user.organization?.teamMembers && user.organization.teamMembers.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-2xl shadow-lg p-6"
        >
          <div className="flex items-center space-x-3 mb-6">
            <FaUsers className="h-6 w-6 text-primary" />
            <h3 className="text-xl font-semibold text-gray-900">Team Members ({user.organization.teamMembers.length})</h3>
          </div>
          <div className="space-y-4">
            {user.organization.teamMembers.map((member, index) => (
              <div
                key={member._id || index}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => navigate(`/users/${member._id}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-primary to-primaryDark rounded-full flex items-center justify-center text-white text-sm font-medium">
                      {getInitials(member.name)}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{member.name}</h4>
                      <p className="text-sm text-gray-500">{member.email}</p>
                      <div className="flex items-center space-x-4 mt-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          member.role === 'agency_admin' ? 'bg-blue-100 text-blue-800' :
                          member.role === 'agency_member' ? 'bg-green-100 text-green-800' :
                          member.role === 'member' ? 'bg-gray-100 text-gray-800' :
                          'bg-purple-100 text-purple-800'
                        }`}>
                          {member.role === 'agency_admin' ? 'Agency Admin' :
                           member.role === 'agency_member' ? 'Agency Member' :
                           member.role === 'member' ? 'Member' :
                           member.role || 'Unknown'}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          member.emailVerified ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {member.emailVerified ? 'Verified' : 'Unverified'}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          member.disabled ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {member.disabled ? 'Disabled' : 'Active'}
                        </span>
                        <span className="text-xs text-gray-500">
                          Joined: {formatDate(member.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="ml-4">
                    <FaEye className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

    </div>
  );
};

export default UserDetail; 